import type { Ref, ReactNode } from 'react';

import styles from './Print.module.css';

/**
 * The paper sizes Kenyan facilities actually have (PLAN.md §7).
 *
 * `receipt-80mm` is the 80 mm thermal roll on every cashier's counter; its height is
 * whatever the content needs, so a short receipt does not feed a whole page of paper.
 * `label-50x25` is the common specimen and dispensing label. `wristband` is the
 * 279 × 25 mm (11 × 1 in) direct-thermal band used for admissions.
 *
 * The screen sizes are in Print.module.css; these are the printed page. Keep them together.
 */
export type PaperSize = 'receipt-80mm' | 'a5' | 'a4' | 'label-50x25' | 'wristband';

export interface PaperSpec {
  /** Shown in print pickers and the development gallery. */
  label: string;
  width: string;
  /** A CSS length, or `content` for a continuous roll: as long as what is on it. */
  height: string;
}

export const PAPER: Record<PaperSize, PaperSpec> = {
  'receipt-80mm': { label: '80 mm thermal receipt', width: '80mm', height: 'content' },
  a5: { label: 'A5', width: '148mm', height: '210mm' },
  a4: { label: 'A4', width: '210mm', height: '297mm' },
  'label-50x25': { label: '50 × 25 mm label', width: '50mm', height: '25mm' },
  wristband: { label: 'Wristband, 279 × 25 mm', width: '279mm', height: '25mm' },
};

const PX_PER_MM = 96 / 25.4;

/**
 * Prints one sheet. The browser has a single page size per document, so the size is
 * added as an `@page` rule for the length of the print, and the sheet is marked so
 * the print stylesheet hides the rest of the screen. The rule goes in a constructed
 * stylesheet, not a `<style>` element, so the Content Security Policy needs no
 * 'unsafe-inline' for a size that varies per sheet (ADR 0037).
 *
 * Silent printing (a local helper that skips the browser dialog) can be added later
 * behind the same call.
 */
export function printSheet(element: HTMLElement, win: Window = window): void {
  const paper = element.dataset['paper'];
  const spec = paper === undefined ? undefined : PAPER[paper as PaperSize];
  if (spec === undefined) throw new Error(`printSheet: unknown paper size ${String(paper)}`);
  const height =
    spec.height === 'content'
      ? `${String(Math.ceil(element.getBoundingClientRect().height / PX_PER_MM))}mm`
      : spec.height;

  const pageSize = new CSSStyleSheet();
  // margin 0: the sheet's own padding is the margin, so screen and paper agree.
  pageSize.replaceSync(`@page { size: ${spec.width} ${height}; margin: 0; }`);
  const doc = win.document;
  doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, pageSize];
  element.dataset['printing'] = 'true';

  let done = false;
  const cleanUp = () => {
    if (done) return;
    done = true;
    doc.adoptedStyleSheets = doc.adoptedStyleSheets.filter((sheet) => sheet !== pageSize);
    delete element.dataset['printing'];
    win.removeEventListener('afterprint', cleanUp);
  };
  win.addEventListener('afterprint', cleanUp);
  try {
    win.print();
  } finally {
    // Chrome's print() returns only after the dialog closes and may not fire
    // afterprint; Safari fires it. Whichever happens first cleans up once.
    cleanUp();
  }
}

export interface PrintSheetProps {
  paper: PaperSize;
  /** Names the document for screen readers and the gallery, e.g. "Payment receipt". */
  label?: string;
  /** The element `printSheet` is given when the person prints this document. */
  ref?: Ref<HTMLElement>;
  children: ReactNode;
}

/** One printed document, at the real size of its paper. */
export function PrintSheet({ paper, label, ref, children }: PrintSheetProps) {
  return (
    <article
      ref={ref}
      className={styles.sheet}
      data-paper={paper}
      {...(label === undefined ? {} : { 'aria-label': label })}
    >
      {children}
    </article>
  );
}

export interface PrintHeaderProps {
  /** The facility printing this. Every printed document says where it came from. */
  facility: string;
  /** Address, phone, KRA PIN — one short line each. */
  facilityLines?: readonly string[];
  /** What the document is: "Payment receipt", "Daily collections". */
  title: string;
}

export function PrintHeader({ facility, facilityLines = [], title }: PrintHeaderProps) {
  return (
    <header className={styles.header}>
      <div className={styles.facility}>{facility}</div>
      {facilityLines.map((line) => (
        <div key={line} className={styles.facilityLine}>
          {line}
        </div>
      ))}
      <div className={styles.title}>{title}</div>
    </header>
  );
}

export interface PrintLine {
  label: string;
  value: string;
  /** Identifiers (receipt no., patient no., M-Pesa code) print in the mono face. */
  identifier?: boolean;
}

/** Label and value pairs, the way a paper form reads. */
export function PrintLines({ items }: { items: readonly PrintLine[] }) {
  return (
    <dl className={styles.lines}>
      {items.map((item) => (
        <div key={item.label} className={styles.linePair}>
          <dt className={styles.lineLabel}>{item.label}</dt>
          <dd
            className={
              item.identifier === true ? `${styles.lineValue} ${styles.mono}` : styles.lineValue
            }
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A plain printed table. Write the `thead`, `tbody` and `tfoot` inline; put
 * `data-numeric` on money and quantity cells so they align on tabular figures.
 */
export function PrintTable({ children }: { children: ReactNode }) {
  return <table className={styles.table}>{children}</table>;
}

/** An identifier in the mono face, for use inside printed prose and table cells. */
export function PrintIdentifier({ children }: { children: string }) {
  return <span className={styles.mono}>{children}</span>;
}

export interface BarcodeAreaProps {
  /** The identifier the symbol encodes, printed underneath for people to read. */
  value: string;
  /** 15 mm instead of 10 mm, for labels scanned from a rack. */
  tall?: boolean;
  /** The rendered symbol, once a barcode renderer exists. Left blank until then. */
  children?: ReactNode;
}

/**
 * The clear space a barcode is printed into. It stays empty of rules and text on
 * paper so a scanner reads only the symbol; the value underneath is what a person
 * reads aloud or types when the scan fails.
 */
export function BarcodeArea({ value, tall = false, children }: BarcodeAreaProps) {
  return (
    <div>
      <div className={styles.barcode} data-tall={tall || undefined}>
        {children ?? (
          <span className={styles.barcodeHint} aria-hidden="true">
            Barcode area
          </span>
        )}
      </div>
      <span className={styles.barcodeValue}>{value}</span>
    </div>
  );
}

export function PrintFooter({ children }: { children: ReactNode }) {
  return <footer className={styles.footer}>{children}</footer>;
}
